import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import emailjs from 'emailjs-com';
import productsData from './data/products.json';

import MedicineProducts from './components/MedicineProducts';

// EmailJS configuration - replace these with your actual values from the EmailJS dashboard
const EMAILJS_SERVICE_ID = 'service_un6gtw7'; // e.g., 'service_xxxxx'
const EMAILJS_TEMPLATE_ID = 'template_32rdvls'; // e.g., 'template_xxxxx'
const EMAILJS_USER_ID = 'nEtABjQOG3kZjmzbn'; // e.g., 'user_xxxxx' or 'public_xxxxx'

// Convert your logo to base64 - using a placeholder for now
// Convert your logo to base64 or render in a premium container
const VarminLogo = ({ isScrolled }) => (
  <div className="flex items-center space-x-2">
    <div className={`p-1 rounded-xl transition-all duration-300 flex items-center justify-center ${
      isScrolled ? 'bg-slate-50 shadow-sm border border-slate-200/50' : 'bg-white shadow-lg'
    }`}>
      <div className='w-16 h-8 vermin-logo'></div>
    </div>
  </div>
);

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Contact form state
  const [contactStatus, setContactStatus] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [visitorCount, setVisitorCount] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Increment and fetch the visitor count from the backend file
    fetch('/visitorCounter.json', { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        let count = data.count || 0;
        count++;
        setVisitorCount(count);
        // Update the file (only works if backend allows write, otherwise this is a placeholder)
        fetch('/visitorCounter.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ count })
        });
      })
      .catch(() => setVisitorCount('N/A'));
  }, []);

  const scrollToSection = (sectionId, event = null) => {
    if (event) {
      event.preventDefault();
    }
    
    setActiveSection(sectionId);
    
    // Add a delay to ensure scrolling positions correctly
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 80, // Adjust for fixed header
          behavior: 'smooth'
        });
      }
    }, 0);
    
    if (window.history.pushState) {
      const newUrl = `${window.location.pathname}#${sectionId}`;
      window.history.pushState({}, '', newUrl);
    }
  };

  // EmailJS contact form submit handler
  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactStatus(null);
    emailjs.sendForm(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      e.target,
      EMAILJS_USER_ID
    )
    .then((result) => {
      setContactStatus({ type: 'success', message: 'Message sent successfully!' });
      e.target.reset();
    }, (error) => {
      setContactStatus({ type: 'error', message: 'Failed to send message.' });
    })
    .finally(() => setContactLoading(false));
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'products', label: 'Products' },
    { id: 'services', label: 'Services' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 antialiased">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-slate-900/90 backdrop-blur-md shadow-lg border-b border-slate-700/50 py-2' 
          : 'bg-transparent py-4'
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <VarminLogo isScrolled={isScrolled} />
            
            <div className="hidden md:flex space-x-1 items-center">
              {navLinks.map((link) => (
                <a 
                  key={link.id}
                  href={`#${link.id}`} 
                  onClick={(e) => scrollToSection(link.id, e)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 relative ${
                    activeSection === link.id 
                      ? (isScrolled ? 'text-emerald-400 bg-emerald-500/10' : 'text-accent-gold bg-white/10') 
                      : (isScrolled ? 'text-slate-300 hover:text-emerald-400 hover:bg-slate-800' : 'text-white/80 hover:text-white hover:bg-white/5')
                  }`}
                >
                  {link.label}
                  {activeSection === link.id && (
                    <span className={`absolute bottom-0 left-4 right-4 h-0.5 rounded-full ${
                      isScrolled ? 'bg-emerald-400' : 'bg-accent-gold'
                    }`} />
                  )}
                </a>
              ))}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                className={`p-2 rounded-lg transition-colors focus:outline-none ${
                  isScrolled ? 'text-slate-200 hover:bg-slate-800' : 'text-white hover:bg-white/10'
                }`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Open menu"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav className="md:hidden fixed top-20 left-4 right-4 bg-slate-900/95 backdrop-blur-lg border border-slate-800 rounded-2xl shadow-2xl z-40 overflow-hidden max-h-[calc(100vh-6rem)] overflow-y-auto animate-fade-in-up">
          <div className="flex flex-col space-y-1 p-4">
            {navLinks.map((link) => (
              <a 
                key={link.id}
                href={`#${link.id}`} 
                onClick={(e) => {
                  scrollToSection(link.id, e);
                  setMobileMenuOpen(false);
                }}
                className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 text-left ${
                  activeSection === link.id 
                    ? 'text-accent-gold bg-white/5' 
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="h-px bg-slate-800 my-2" />
            <Link 
              to="/privacy-policy" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 rounded-xl text-base font-semibold text-slate-400 hover:text-white hover:bg-white/5 text-left"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 rounded-xl text-base font-semibold text-slate-400 hover:text-white hover:bg-white/5 text-left"
            >
              Terms of Service
            </Link>
          </div>
        </nav>
      )}

      {/* Hero Section */}
      <main>
        <section id="home" className="relative min-h-screen mb-2 flex items-center justify-center overflow-hidden">
          {/* Base Premium Gradients */}
          <div className="absolute inset-0 bg-premium-gradient"></div>
          
          {/* Subtle Ambient Radial Lighting Glows */}
          <div className="absolute inset-0 pointer-events-none opacity-40">
            <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px]" />
            <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-amber-500/5 blur-[150px]" />
          </div>

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:40px_40px] opacity-60"></div>
          
          <div className="absolute inset-0 opacity-15">
            <div className="floating-particles"></div>
          </div>
          
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 mt-16 md:mt-24">
            <div className="hero-content backdrop-blur-md bg-white/[0.02] border border-white/[0.07] p-6 sm:p-10 md:p-16 rounded-2xl sm:rounded-3xl shadow-2xl relative">
              {/* Outer decorative ring */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500/20 to-amber-500/20 rounded-3xl blur opacity-30 -z-10"></div>
              
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 mb-6 animate-fade-in-up">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Next-Gen Therapeutics
              </span>

              <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-6 animate-fade-in-up tracking-tight leading-tight md:leading-none">
                <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-yellow-300 bg-clip-text text-transparent drop-shadow-sm">Varmin</span>
                <br />
                <span className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-light text-accent-gold tracking-widest uppercase mt-2 block">Pharmaceuticals</span>
              </h1>
              
              <p className="text-sm sm:text-lg md:text-xl lg:text-2xl mb-8 md:mb-10 text-slate-300 max-w-2xl mx-auto font-light leading-relaxed animate-fade-in-up-delay">
                Leading the Future of Healthcare with Quality Formulations, Trusted Doctor Partnerships, and Uncompromising Excellence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up-delay-2">
                <button 
                  onClick={() => scrollToSection('products')}
                  className="btn-premium w-full sm:w-auto text-base py-3.5 px-8"
                >
                  Explore Catalog
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="btn-premium-outline w-full sm:w-auto text-base py-3.5 px-8"
                >
                  Partner With Us
                </button>
              </div>
            </div>
          </div>

          {/* 3D Floating Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="pill-3d pill-1"></div>
            <div className="pill-3d pill-2"></div>
            <div className="pill-3d pill-3"></div>
            <div className="molecule-3d molecule-1"></div>
            <div className="molecule-3d molecule-2"></div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="relative z-20 -mt-10 sm:-mt-16 md:-mt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-slate-700/50 p-6 sm:p-8 md:p-10 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-4 divide-y md:divide-y-0 md:divide-x divide-slate-700/50 animate-fade-in-up-delay-2">
            <div className="flex flex-col items-center justify-center text-center p-4 sm:p-6 md:p-4">
              <span className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">3+</span>
              <span className="text-sm font-semibold tracking-wider uppercase text-slate-300 mt-2">Premium Formulations</span>
              <span className="text-xs text-slate-400 mt-1 max-w-[200px]">Rigorously tested, high-efficacy custom formulas</span>
            </div>
            <div className="flex flex-col items-center justify-center text-center p-4 sm:p-6 md:p-4">
              <span className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">150+</span>
              <span className="text-sm font-semibold tracking-wider uppercase text-slate-300 mt-2">Partnered Clinics</span>
              <span className="text-xs text-slate-400 mt-1 max-w-[200px]">Trusted by leading medical practitioners</span>
            </div>
            <div className="flex flex-col items-center justify-center text-center p-4 sm:p-6 md:p-4">
              <span className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">100%</span>
              <span className="text-sm font-semibold tracking-wider uppercase text-slate-300 mt-2">Quality Standards</span>
              <span className="text-xs text-slate-400 mt-1 max-w-[200px]">GMP compliant production and sourcing</span>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-16 sm:py-24 bg-[#0f172a]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-20">
              <h2 className="heading-premium text-3xl md:text-5xl font-black text-white mb-6">
                About <span className="text-accent-gold">Varmin</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
                We are a dynamic pharmaceutical startup dedicated to bringing high-quality medicines to market through strategic partnerships with healthcare professionals and state-of-the-art research.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
              <div className="space-y-6 sm:space-y-8">
                <article className="card-3d p-6 sm:p-8 bg-slate-800/60 border border-slate-700/50 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-emerald-500/5 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">Our Vision</h3>
                  </div>
                  <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                    To revolutionize healthcare accessibility by bridging the gap between pharmaceutical innovation and patient care through strategic doctor partnerships and quality medicine distribution.
                  </p>
                </article>
                
                <article className="card-3d p-6 sm:p-8 bg-slate-800/60 border border-slate-700/50 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-amber-500/5 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-400">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">Our Mission</h3>
                  </div>
                  <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                    To establish Varmin as a trusted pharmaceutical brand by delivering effective, safe, and affordable medicines while building lasting relationships with healthcare providers.
                  </p>
                </article>
              </div>

              <div className="relative">
                {/* Decorative backgrounds */}
                <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-500/10 to-amber-500/10 rounded-3xl blur-2xl opacity-70"></div>
                <div className="absolute -bottom-6 -right-6 w-24 sm:w-32 h-24 sm:h-32 bg-amber-500/10 rounded-full blur-xl"></div>
                <div className="absolute -top-6 -left-6 w-24 sm:w-32 h-24 sm:h-32 bg-emerald-500/10 rounded-full blur-xl"></div>
                
                <div className="relative image-3d">
                  <img 
                    src="https://images.pexels.com/photos/356040/pexels-photo-356040.jpeg" 
                    alt="Pharmaceutical Research and Development at Varmin Pharmaceuticals" 
                    className="w-full h-60 sm:h-96 lg:h-[450px] object-cover rounded-2xl shadow-xl border border-white/50"
                  />
                </div>
              </div>
            </div>

            {/* Core Values Section */}
            <div className="mt-20 sm:mt-24">
              <h3 className="text-center text-lg sm:text-xl font-bold tracking-widest text-slate-400 uppercase mb-8 sm:mb-12">Our Core Values</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
                {[
                  { title: 'Efficacy', desc: 'Formulated for optimal therapeutic action and absorption.', color: 'from-emerald-500 to-teal-600' },
                  { title: 'Trust', desc: 'Partnering transparently with medical professionals globally.', color: 'from-blue-500 to-indigo-600' },
                  { title: 'Quality', desc: 'Manufactured in fully certified, GMP-compliant facilities.', color: 'from-amber-500 to-yellow-600' },
                  { title: 'Care', desc: 'Prioritizing patient outcomes and accessible pricing.', color: 'from-rose-500 to-pink-600' }
                ].map((val, idx) => (
                  <div key={idx} className="bg-slate-800/60 border border-slate-700/50 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm hover:-translate-y-1 transition-all duration-300">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${val.color} mb-3 sm:mb-4 flex items-center justify-center text-white font-bold text-sm`}>
                      {val.title[0]}
                    </div>
                    <h4 className="font-bold text-white text-sm sm:text-base mb-1.5 sm:mb-2">{val.title}</h4>
                    <p className="text-2xs sm:text-xs text-slate-400 leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>



        {/* Products Section */}
        <MedicineProducts onInquireProduct={(prodName) => {
          const subjectInput = document.querySelector('input[name="subject"]');
          if (subjectInput) {
            subjectInput.value = `Inquiry regarding ${prodName}`;
          }
          const messageInput = document.querySelector('textarea[name="message"]');
          if (messageInput) {
            messageInput.value = `Hello, I would like to inquire about the availability, composition, and bulk pricing of ${prodName} for my healthcare practice.`;
          }
          scrollToSection('contact');
        }} />
        


        {/* Services Section */}
        <section id="services" className="py-16 sm:py-24 bg-[#0f172a] border-t border-slate-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-20">
              <h2 className="heading-premium text-3xl md:text-5xl font-black text-white mb-6">
                Our <span className="text-accent-gold">Services</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
                Comprehensive pharmaceutical services designed to support healthcare professionals and patients with premium solutions.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
              <div className="space-y-6">
                {[
                  {
                    title: "Doctor Partnership Program",
                    description: "Strategic collaborations with healthcare professionals for product promotion, medical insight sharing, and tailored formulations.",
                    icon: (
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    ),
                    bg: "bg-emerald-500/10"
                  },
                  {
                    title: "Quality Assurance & Standards",
                    description: "Rigorous laboratory testing and quality control processes to ensure complete safety and high efficacy for every batch.",
                    icon: (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    ),
                    bg: "bg-blue-500/10"
                  },
                  {
                    title: "Strategic Market Distribution",
                    description: "Optimized cold chain logistics and secure distribution networks delivering products directly to clinics and pharmacies.",
                    icon: (
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7a3 3 0 11-6 0 3 3 0 016 0zM12 21V11a2 2 0 00-2-2H8a2 2 0 00-2 2v10m6 0h6a2 2 0 002-2v-5a2 2 0 00-2-2h-3" />
                      </svg>
                    ),
                    bg: "bg-amber-500/10"
                  },
                  {
                    title: "Medical & Healthcare Education",
                    description: "Providing comprehensive medical literature, webinars, and education packets for clinics and partners.",
                    icon: (
                      <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    ),
                    bg: "bg-rose-500/10"
                  }
                ].map((service, index) => (
                  <article key={index} className="service-card-3d flex items-start space-x-4 sm:space-x-6 p-5 sm:p-6 bg-slate-800/60 border border-slate-700/50 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-emerald-500/5 transition-all duration-300">
                    <div className={`flex-shrink-0 w-12 h-12 ${service.bg} rounded-xl flex items-center justify-center`}>
                      {service.icon}
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{service.title}</h3>
                      <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">{service.description}</p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="relative">
                {/* Decorative backgrounds */}
                <div className="absolute -inset-4 bg-gradient-to-tr from-amber-500/10 to-emerald-500/10 rounded-3xl blur-2xl opacity-70"></div>
                <div className="absolute -bottom-6 -left-6 w-24 sm:w-32 h-24 sm:h-32 bg-emerald-500/10 rounded-full blur-xl"></div>
                <div className="absolute -top-6 -right-6 w-24 sm:w-32 h-24 sm:h-32 bg-amber-500/10 rounded-full blur-xl"></div>
                
                <div className="relative image-3d">
                  <img 
                    src="https://images.unsplash.com/photo-1471864190281-a93a3070b6de" 
                    alt="Pharmaceutical Products and Services at Varmin Pharmaceuticals" 
                    className="w-full h-60 sm:h-96 lg:h-[450px] object-cover rounded-2xl shadow-xl border border-white/50"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-premium-gradient text-white relative overflow-hidden">
          {/* Subtle lighting rings */}
          <div className="absolute inset-0 pointer-events-none opacity-20">
            <div className="absolute bottom-[-100px] left-[-100px] w-[350px] h-[350px] rounded-full bg-emerald-500/20 blur-[100px]" />
            <div className="absolute top-[-100px] right-[-100px] w-[350px] h-[350px] rounded-full bg-amber-500/20 blur-[100px]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-20">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-white/10 text-emerald-300 border border-white/10 mb-4">
                Connect With Us
              </span>
              <h2 className="heading-premium text-3xl md:text-5xl font-black mb-6">
                Get In <span className="text-accent-gold">Touch</span>
              </h2>
              <p className="text-lg md:text-xl text-emerald-100/80 max-w-3xl mx-auto leading-relaxed">
                Ready to partner or have queries? Contact our clinical or corporate team to discuss collaboration opportunities.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="contact-card-3d p-8 bg-white/[0.03] border border-white/[0.08] backdrop-blur-md rounded-3xl shadow-xl" itemScope itemType="https://schema.org/Organization">
                  <h3 className="text-2xl font-black mb-8 text-accent-gold">Company Headquarters</h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20 mt-1 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold text-slate-100 text-lg" itemProp="name">Varmin Pharmaceuticals Pvt. Ltd.</p>
                        <p className="text-slate-300 text-sm mt-1 leading-relaxed" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                          <span itemProp="streetAddress">Corp. Office: 708, Royal Unicorn, Twin Towers, Wakad</span>
                          <span className="block" itemProp="addressLocality">Pune - 411057, Maharashtra, India</span>
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 00-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Email Correspondence</p>
                        <p className="text-slate-100 text-base" itemProp="email">varminpharmaceuticals@gmail.com</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Corporate Hotline</p>
                        <p className="text-slate-100 text-base font-bold" itemProp="telephone">+91 94521 01977</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-form-3d">
                <form className="space-y-6 bg-white/[0.03] border border-white/[0.08] backdrop-blur-md p-8 md:p-10 rounded-3xl shadow-xl relative" onSubmit={handleContactSubmit}>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500/10 to-amber-500/10 rounded-3xl blur opacity-25 -z-10"></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Your Name</label>
                      <input
                        type="text"
                        name="from_name"
                        className="w-full px-4 py-3 bg-white/[0.06] hover:bg-white/[0.09] focus:bg-white/[0.12] border border-white/[0.15] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 text-white placeholder-slate-400 transition-all duration-300 rounded-xl outline-none"
                        placeholder="Dr. John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Email Address</label>
                      <input
                        type="email"
                        name="from_email"
                        className="w-full px-4 py-3 bg-white/[0.06] hover:bg-white/[0.09] focus:bg-white/[0.12] border border-white/[0.15] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 text-white placeholder-slate-400 transition-all duration-300 rounded-xl outline-none"
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      className="w-full px-4 py-3 bg-white/[0.06] hover:bg-white/[0.09] focus:bg-white/[0.12] border border-white/[0.15] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 text-white placeholder-slate-400 transition-all duration-300 rounded-xl outline-none"
                      placeholder="Collaboration / Product Inquiry"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Your Message</label>
                    <textarea
                      rows={4}
                      name="message"
                      className="w-full px-4 py-3 bg-white/[0.06] hover:bg-white/[0.09] focus:bg-white/[0.12] border border-white/[0.15] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 text-white placeholder-slate-400 transition-all duration-300 rounded-xl outline-none resize-none"
                      placeholder="Write your details here..."
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full btn-premium py-3.5 rounded-xl font-bold text-sm tracking-wide shadow-lg"
                    disabled={contactLoading}
                  >
                    {contactLoading ? 'Sending...' : 'Send Inquiry Message'}
                  </button>
                  {contactStatus && (
                    <div className={`mt-4 text-center font-bold text-sm px-4 py-2 rounded-xl backdrop-blur-md ${
                      contactStatus.type === 'success' 
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}>
                      {contactStatus.message}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-white border-t border-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <VarminLogo isScrolled={false} />
              <p className="mt-6 text-slate-400 text-sm leading-relaxed max-w-xs">
                Leading pharmaceutical innovation with premium medications, certified production facilities, and trusted healthcare collaborations.
              </p>
              
              <div className="mt-6">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-xs font-semibold text-emerald-400">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  Visitor Trust Counter: {visitorCount !== null ? visitorCount : 'Loading...'}
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-base font-bold uppercase tracking-wider text-slate-200 mb-6">Quick Navigation</h3>
              <ul className="space-y-3.5 text-sm text-slate-400">
                <li><a href="#about" className="hover:text-emerald-400 hover:underline transition-all duration-200">About Us</a></li>
                <li><a href="#products" className="hover:text-emerald-400 hover:underline transition-all duration-200">Product Portfolio</a></li>
                <li><a href="#services" className="hover:text-emerald-400 hover:underline transition-all duration-200">Key Services</a></li>
                <li><a href="#contact" className="hover:text-emerald-400 hover:underline transition-all duration-200">Contact Team</a></li>
                <li><Link to="/privacy-policy" className="hover:text-emerald-400 hover:underline transition-all duration-200">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-emerald-400 hover:underline transition-all duration-200">Terms of Service</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-base font-bold uppercase tracking-wider text-slate-200 mb-6">Contact Channels</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                Varmin Pharmaceuticals Pvt. Ltd.<br/>
                Corp. Office: 708, Royal Unicorn, Twin Towers, Wakad Pune-411057, Maharashtra, India
              </p>
              <a href="mailto:varminpharmaceuticals@gmail.com" className="text-emerald-400 hover:text-emerald-300 font-semibold text-sm transition-colors duration-200 block mb-2">
                varminpharmaceuticals@gmail.com
              </a>
              
              <div className="flex space-x-4 mt-6">
                <a href="https://www.linkedin.com/company/varmin-pharmaceuticals" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all duration-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 11.268h-3v-5.604c0-1.337-.025-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.601v5.595z"/></svg>
                </a>
                <a href="https://www.facebook.com/varminpharmaceuticals" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all duration-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.733 0-1.325.592-1.325 1.326v21.348c0 .733.592 1.326 1.325 1.326h11.495v-9.294h-3.128v-3.622h3.128v-2.672c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.733 0 1.325-.593 1.325-1.326v-21.349c0-.734-.592-1.326-1.325-1.326z"/></svg>
                </a>
                <a href="https://twitter.com/varminpharma" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all duration-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557a9.93 9.93 0 0 1-2.828.775 4.932 4.932 0 0 0 2.165-2.724c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 0 0-8.384 4.482c-4.086-.205-7.713-2.164-10.141-5.144a4.822 4.822 0 0 0-.666 2.475c0 1.708.87 3.216 2.188 4.099a4.904 4.904 0 0 1-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.936 4.936 0 0 1-2.224.084c.627 1.956 2.444 3.377 4.6 3.417a9.867 9.867 0 0 1-6.102 2.104c-.396 0-.787-.023-1.175-.069a13.945 13.945 0 0 0 7.548 2.212c9.057 0 14.009-7.513 14.009-14.009 0-.213-.005-.425-.014-.636a10.012 10.012 0 0 0 2.457-2.548z"/></svg>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-900 mt-12 pt-8 text-center text-slate-500 text-xs">
            <p>&copy; {new Date().getFullYear()} Varmin Pharmaceuticals Pvt. Ltd. All rights reserved.</p>
            <p className="mt-2 text-slate-600">
              Designed & managed by <a href="https://vanvidtech.w3spaces.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">VanVid Technologies</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;